import React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import IconButton from '@mui/material/IconButton'
import type { SxProps } from '@mui/material/styles'

import { Icon } from '@mxenabled/mx-icons'
import { ChevronRight } from '@mxenabled/mx-icons'

import { H3, P, Text } from '@mxenabled/mxui'

import { WidgetContainerProvider } from '../context'
import { useDimensions } from '../hooks/useDimensions'

interface CardContainerProps {
  className?: string
  closeLabel?: string
  contentStyles?: SxProps
  onClose?: () => void
  onPrimaryCtaClick?: () => void
  primaryCtaLabel?: string
  primaryCtaLabelAria?: string
  subTitle?: string
  sx?: SxProps
  title?: string
  /**
   * Accessible name for the widget region when no visible `title` is rendered.
   * When `title` is present, the region is labelled by the title text instead.
   */
  titleAria?: string
  /**
   * Extra summary read after the title when the screen-reader cursor lands on
   * the region (e.g. "$240 left in budgets"). Appended to the region's
   * accessible name via a visually-hidden node so it is announced but not shown.
   */
  summaryAria?: string
}

const MiniWidgetContainer: React.FC<React.PropsWithChildren<CardContainerProps>> = ({
  children,
  className = 'mx-cmn-mini-widget',
  closeLabel,
  contentStyles,
  onClose,
  onPrimaryCtaClick,
  primaryCtaLabel = '',
  primaryCtaLabelAria = '',
  subTitle = '',
  sx = {},
  title = '',
  titleAria,
  summaryAria,
}) => {
  const [containerRef, { height, width }] = useDimensions()
  const titleId = React.useId()
  const summaryId = React.useId()
  const hasVisibleTitle = Boolean(title)

  // aria-labelledby wins when a title is visible (name = title [+ summary]);
  // otherwise fall back to a plain aria-label.
  const labelledBy = hasVisibleTitle
    ? [titleId, summaryAria && summaryId].filter(Boolean).join(' ')
    : undefined

  return (
    <Card
      aria-label={
        hasVisibleTitle
          ? undefined
          : [titleAria, summaryAria].filter(Boolean).join(', ') || undefined
      }
      aria-labelledby={labelledBy}
      className={className}
      component="section"
      sx={{ touchAction: 'manipulation', ...sx }}
    >
      {summaryAria && (
        // aria-hidden keeps this out of the browse cursor as a standalone stop;
        // aria-labelledby still pulls the text into the region's accessible name.
        <Box
          aria-hidden="true"
          id={summaryId}
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {summaryAria}
        </Box>
      )}
      <CardHeader
        sx={{ pb: 8 }}
        title={
          <>
            <Box
              sx={{
                alignItems: 'center',
                display: 'grid',
                gap: 2,
                gridTemplateColumns: '1fr auto',
                pb: 4,
              }}
            >
              {title && <H3 id={titleId}>{title}</H3>}

              {onClose ? (
                <IconButton aria-label={closeLabel} onClick={onClose} size="small">
                  <Icon name="close" size={24} />
                </IconButton>
              ) : (
                onPrimaryCtaClick && (
                  <Button
                    aria-label={primaryCtaLabelAria}
                    endIcon={<ChevronRight />}
                    onClick={onPrimaryCtaClick}
                    size="small"
                  >
                    <Text bold={true} variant="body2">
                      {primaryCtaLabel}
                    </Text>
                  </Button>
                )
              )}
            </Box>

            {subTitle && (
              <P color="text.secondary" sx={{ fontSize: 15, lineHeight: '20px' }} variant="body2">
                {subTitle}
              </P>
            )}
          </>
        }
      />

      <CardContent ref={containerRef} sx={{ p: 0, ...contentStyles }}>
        <WidgetContainerProvider availableHeight={height} availableWidth={width}>
          {children}
        </WidgetContainerProvider>
      </CardContent>
    </Card>
  )
}

export default MiniWidgetContainer
